"use client";

import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Separator } from "@/components/ui/separator";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 animate-pulse bg-muted/50 rounded-md" />
        <div className="h-6 w-1/2 animate-pulse bg-muted/50 rounded-md" />
        <div className="aspect-[16/9] w-full animate-pulse bg-muted/50 rounded-md" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse bg-muted/50 rounded-md" />
          <div className="h-4 w-full animate-pulse bg-muted/50 rounded-md" />
          <div className="h-4 w-5/6 animate-pulse bg-muted/50 rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-muted-foreground">Unable to load this article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorName = post.author?.name || "MetalLine Exteriors";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const authorInitials = authorName
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Insights</span>
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center px-3 py-1 text-xs font-medium tracking-wider uppercase bg-accent text-accent-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6 leading-tight">
        {post.title}
      </h1>

      {}
      {post.excerpt && (
        <div
          className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 font-default italic"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-6 mb-10 pb-8 border-b border-border">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
              {authorInitials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              Expert Author
            </span>
            <span className="text-sm font-medium text-foreground">
              {authorName}
            </span>
          </div>
        </div>

        {formattedDate && (
          <>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>{formattedDate}</span>
            </div>
          </>
        )}
      </div>

      {}
      {post.featuredImage && (
        <figure className="mb-12">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto rounded-lg shadow-lg object-cover aspect-[16/9]"
          />
        </figure>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-foreground leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:md:text-lg [&>p]:leading-relaxed [&>h2]:font-default [&>h2]:text-2xl [&>h2]:md:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-xl [&>h3]:md:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol]:space-y-2 [&>blockquote]:border-l-4 [&>blockquote]:border-ring [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-8 [&>img]:rounded-md [&>img]:my-8 [&>img]:w-full [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:opacity-80"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wider mr-2">
              Topics
            </span>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="inline-flex items-center px-3 py-1 text-xs bg-secondary text-secondary-foreground rounded-md"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      <Card className="mt-16 bg-card border border-border shadow-md">
        <CardContent className="p-8">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <Avatar className="h-20 w-20 shrink-0">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-lg">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-primary" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  About the Expert
                </span>
              </div>
              <h3 className="font-default text-xl font-semibold text-foreground mb-2">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                MetalLine Exteriors is Calgary's premier choice for high-end metal roofing, architectural cladding, and luxury exterior renovations. We combine precision engineering with architectural beauty.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-4">
            <div className="aspect-[4/3] w-full animate-pulse bg-muted/50 rounded-md" />
            <div className="h-5 w-3/4 animate-pulse bg-muted/50 rounded-md" />
            <div className="h-4 w-1/2 animate-pulse bg-muted/50 rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        No related projects or articles available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";

        return (
          <Link
            data-index={i}
            key={post.id}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="h-full bg-card border border-border hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              {post.featuredImage && (
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <CardContent className="p-6">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-block text-xs uppercase tracking-wider text-primary mb-3 font-medium">
                    {post.categories[0].name}
                  </span>
                )}
                <h3 className="font-default text-lg font-semibold text-foreground mb-3 leading-snug group-hover:opacity-80 transition-opacity">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <div
                    className="text-sm text-muted-foreground line-clamp-2 mb-4"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  {formattedDate && <span>{formattedDate}</span>}
                  <span className="inline-flex items-center gap-1 text-primary font-medium">
                    Read More
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

export default function Article() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section data-section-id="17447"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mx-auto max-w-6xl mt-24 md:mt-32">
          <div className="mb-12 text-center">
            <span className="inline-block text-xs uppercase tracking-widest text-primary mb-4 font-medium">
              Continue Reading
            </span>
            <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              Related Projects & Insights
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover more about our premium exterior solutions, from architectural cladding to high-performance metal roofing systems.
            </p>
          </div>

          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsContent />
          </PostsProvider>

          <div className="mt-12 text-center">
            <Link to="/HomePage">
              <Button variant="outline" size="lg" className="border-border">
                View All Articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}